import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { CaretRight, CheckCircle, XCircle } from "@phosphor-icons/react";

const PC = { gpt: "#10A37F", claude: "#D97757", gemini: "#8E24AA" };
const PL = { gpt: "GPT", claude: "Claude", gemini: "Gemini" };

function fmt(iso) {
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function Citations() {
  const [selected, setSelected] = useState(null);
  const { data: rows, isLoading } = useQuery({
    queryKey: ["citations"], queryFn: async () => (await api.get("/citations?limit=200")).data,
  });

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-8 fade-in">
        <p className="overline text-action">Data</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tighter">Citation Log</h1>
        <p className="mt-1 text-sm text-muted-foreground">Every LLM probe and whether your brand was cited. Click a row to inspect the response.</p>
      </div>

      <Card className="border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-wider">Time</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Provider</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Brand</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Cited</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Mentions</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Rank</TableHead>
              <TableHead className="w-8"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="citations-table">
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
            ) : (rows || []).length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">No citations recorded yet.</TableCell></TableRow>
            ) : (
              rows.map((r) => (
                <TableRow
                  key={r.id}
                  className="cursor-pointer border-border/40 transition-colors duration-150 hover:bg-secondary/40"
                  data-testid={`citation-row-${r.id}`}
                  onClick={() => setSelected(r)}
                >
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmt(r.timestamp)}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1.5 text-sm font-medium" style={{ color: PC[r.provider] }}>
                      <span className="h-2 w-2 rounded-full" style={{ background: PC[r.provider] }} /> {PL[r.provider]}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{r.brand}</TableCell>
                  <TableCell>
                    {r.cited
                      ? <CheckCircle size={18} weight="fill" className="text-success" />
                      : <XCircle size={18} className="text-muted-foreground" />}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{r.mention_count}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{r.rank_position ? `#${r.rank_position}` : "—"}</TableCell>
                  <TableCell><CaretRight size={14} className="text-muted-foreground" /></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto border-border bg-card sm:max-w-md">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading tracking-tight">Probe Detail</SheetTitle>
                <SheetDescription>Full prompt and the model's response for this citation probe.</SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-6">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1.5 rounded-md px-3 py-1 text-sm font-semibold text-white" style={{ background: PC[selected.provider] }}>
                    {PL[selected.provider]}
                  </span>
                  {selected.cited
                    ? <Badge className="bg-success/15 text-success border-0">Cited ×{selected.mention_count}</Badge>
                    : <Badge variant="outline" className="border-border text-muted-foreground">Not cited</Badge>}
                  <Badge variant="outline" className="border-border text-muted-foreground capitalize">{selected.trigger}</Badge>
                </div>

                <div>
                  <p className="overline text-muted-foreground mb-2">Timestamp</p>
                  <p className="font-mono text-sm">{fmt(selected.timestamp)}</p>
                </div>
                <div>
                  <p className="overline text-muted-foreground mb-2">Brand tracked</p>
                  <p className="text-sm">{selected.brand}</p>
                </div>
                <div>
                  <p className="overline text-muted-foreground mb-2">Prompt sent</p>
                  <p className="rounded-md border border-border bg-background p-3 text-sm leading-relaxed">{selected.prompt}</p>
                </div>
                <div>
                  <p className="overline text-muted-foreground mb-2">LLM response</p>
                  <p className="max-h-80 overflow-y-auto rounded-md border border-border bg-background p-3 text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap" data-testid="response-text">
                    {selected.response_text}
                  </p>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
